import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import Navigation from "../components/Navigation";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import Manifesto from "../components/Manifesto";
import Pricing from "../components/Pricing";
import MarketSection from "../components/MarketSection";
import FloorPlans from "../components/FloorPlans";
import Amenities from "../components/Amenities";
import Gallery from "../components/Gallery";
import Walkthrough from "../components/Walkthrough";
import LocationSection from "../components/LocationSection";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import CallFloat from "../components/CallFloat";
import StickyActionBar from "../components/StickyActionBar";
import EnquiryModal from "../components/EnquiryModal";
import FeaturePills from "../components/FeaturePills";
import Preloader from "../components/Preloader";
import CustomCursor from "../components/CustomCursor";
import ScrollProgress from "../components/ScrollProgress";

const MARQUEE_ONE = ["Grade A+ Offices", "52 Acre Integrated District", "Thane West", "35 Office Floors", "Pre-Launch Open"];
const MARQUEE_TWO = ["Private Previews Open", "Limited Pre-Launch Inventory", "EOI Now Invited", "Cadbury Junction"];
const MICRO = ["Pre-Launch Live", "₹33,000 PSF*", "EEH 1 Min", "Metro 3 Min", "35 Office Floors", "52 Acre District", "EOI Open", "Grade A+ Offices"];

const POPUPS = [
  { key: "sugee_pop_main", title: "Pre-register for best benefits", intent: "Pre-Launch EOI" },
  { key: "sugee_pop_scroll", title: "Book your free site visit", intent: "Schedule Site Visit" },
  { key: "sugee_pop_callback", title: "Get a call back within 30 minutes", intent: "Request Call Back" },
  { key: "sugee_pop_exit", title: "Wait — grab the pre-launch price sheet", intent: "Request Brochure" },
];

export default function Landing() {
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, intent: "Pre-Launch EOI", title: undefined });
  const openRef = useRef(false);

  const tryOpen = useCallback((p) => {
    if (openRef.current || sessionStorage.getItem(p.key)) return;
    sessionStorage.setItem(p.key, "1");
    openRef.current = true;
    setModal({ open: true, intent: p.intent, title: p.title });
  }, []);

  const closeModal = () => {
    openRef.current = false;
    setModal((m) => ({ ...m, open: false }));
  };

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      openRef.current = true;
      setModal({ open: true, intent: e.detail?.intent || "Pre-Launch EOI", title: e.detail?.title });
    };
    window.addEventListener("open-enquiry", handler);
    const timers = [14000, 45000].map((d, i) => setTimeout(() => tryOpen(POPUPS[i === 0 ? 0 : 2]), d));
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && window.scrollY / h > 0.55) tryOpen(POPUPS[1]);
    };
    const onExit = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) tryOpen(POPUPS[3]);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onExit);
    return () => {
      window.removeEventListener("open-enquiry", handler);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onExit);
      timers.forEach(clearTimeout);
    };
  }, [tryOpen]);

  return (
    <div data-testid="landing-page" className="bg-background text-foreground overflow-x-clip pb-14">
      <AnimatePresence>{loading && <Preloader />}</AnimatePresence>
      <ScrollProgress />
      <CustomCursor />
      <Navigation />
      <main>
        <Hero />
        <FeaturePills />
        <Marquee items={MICRO} fast small />
        <Marquee items={MARQUEE_ONE} />
        <Manifesto />
        <FloorPlans />
        <Pricing />
        <MarketSection />
        <Amenities />
        <Gallery />
        <Walkthrough />
        <LocationSection />
        <Marquee items={MARQUEE_TWO} />
        <Contact />
      </main>
      <Footer />
      <WhatsAppFloat />
      <CallFloat />
      <StickyActionBar />
      <EnquiryModal open={modal.open} intent={modal.intent} title={modal.title} onClose={closeModal} />
      <div className="grain-overlay" />
    </div>
  );
}
